/**
 * The dynamic half of a service request form.
 *
 * An Ivanti request offering declares its own parameters, so the fields on this
 * form are data, not code. Each `OfferingParameterType` maps to one design
 * system control; the two types the portal cannot honour yet — `ATTACHMENT`
 * (no upload endpoint wired) and `UNKNOWN` (a type this build predates) — are
 * rendered as an explanatory note rather than dropped, because a silently
 * missing required field produces a request IT cannot fulfil.
 */

import {
  Button,
  Checkbox,
  DatePicker,
  DateTimePicker,
  type DateValue,
  Field,
  FilterSelect,
  Input,
  parseDate,
  parseDateTime,
  Textarea,
  Typography,
} from "@norican/react";
import { useState } from "react";

import { AttachmentUploader, usePasteToAttach } from "@/components/attachments";
import { HighlightedField, useFormCheckMessage } from "@/components/common";
import { useT } from "@/i18n";
import type { DirectoryEntry } from "@/lib/api";
import { useFieldHighlight } from "@/lib/field-highlight";
import { checkOfferingAnswer, shownCheck, todayIso } from "@/lib/form-checks";
import { logger } from "@/lib/logger";

import type { OfferingParameter } from "../api/contract";
import { defaultDisplayValue, isEmployeeLookup } from "../lib/offering-lookup";
import { displayText } from "../lib/translatable";
import { BluestarAreasField } from "./bluestar-areas-field";
import { EmployeePicker } from "./employee-picker";
import { LookupSelect } from "./lookup-select";

/** One parameter's collected answer, in the shape `createTicket` expects. */
export interface AnswerState {
  value: string | null;
  valueIds: string[];
  /**
   * Draft `attachment_id`s for an `ATTACHMENT` parameter, uploaded ahead of the
   * ticket. Left undefined for every other type, and travels on the answer's
   * `attachment_ids` rather than `value`/`value_ids`.
   */
  attachmentIds?: string[];
}

export type AnswerMap = Record<string, AnswerState>;

export const EMPTY_ANSWER: AnswerState = { value: null, valueIds: [] };

/** The answer for a parameter, or a blank one. */
export function answerFor(answers: AnswerMap, parameterId: string): AnswerState {
  return answers[parameterId] ?? EMPTY_ANSWER;
}

/**
 * Which half of an answer a directory lookup writes to.
 *
 * Ivanti models the user field as a `SELECT`, whose answers travel as
 * `value_ids`; if a tenant ever types one as free text the id belongs in
 * `value` instead. Reading and writing it through one pair of functions keeps
 * that decision in a single place rather than spread across the picker, the
 * validator and the summary.
 */
function usesValueIds(parameter: OfferingParameter): boolean {
  return parameter.type === "SELECT" || parameter.type === "MULTISELECT";
}

/** The employee id a lookup parameter currently holds, or null. */
export function readEmployeeId(parameter: OfferingParameter, answer: AnswerState): string | null {
  const raw = usesValueIds(parameter) ? (answer.valueIds[0] ?? null) : answer.value;
  return raw == null || raw === "" ? null : raw;
}

/** The answer for a lookup parameter set to (or cleared of) an employee id. */
export function writeEmployeeId(parameter: OfferingParameter, id: string | null): AnswerState {
  if (usesValueIds(parameter)) {
    return { value: null, valueIds: id === null ? [] : [id] };
  }
  return { value: id, valueIds: [] };
}

/**
 * The answers a generated form starts with.
 *
 * Only the directory lookups are seeded. Ivanti defaults the "User name" field
 * to the requester and that default is a RecId; the offering ships
 * `default_display_value` alongside it so the picker can show the name, but the
 * id still has to be in the answers or the form would submit nothing for a
 * field that visibly reads as filled in. Other parameter types are left alone
 * deliberately — their defaults have never been applied here, and applying them
 * now could pre-select an option the tenant has since withdrawn.
 */
export function seedLookupAnswers(parameters: OfferingParameter[]): AnswerMap {
  const answers: AnswerMap = {};
  for (const parameter of parameters) {
    const fallback = parameter.default_value;
    if (!isEmployeeLookup(parameter) || fallback == null || fallback === "") {
      continue;
    }
    answers[parameter.parameter_id] = writeEmployeeId(parameter, fallback);
  }
  return answers;
}

/**
 * Display names for those seeded defaults, keyed by parameter.
 *
 * A read-only summary of a filed request reads its labels out of
 * `parameter.options`, which a lookup answer is by definition not in. Without
 * this the summary would report back the GUID the picker exists to hide.
 */
export function seedLookupDisplayNames(parameters: OfferingParameter[]): Record<string, string> {
  const names: Record<string, string> = {};
  for (const parameter of parameters) {
    const name = defaultDisplayValue(parameter);
    if (isEmployeeLookup(parameter) && name !== null) {
      names[parameter.parameter_id] = name;
    }
  }
  return names;
}

/**
 * Whether a parameter is currently in play.
 *
 * `depends_on` makes a field conditional on another's selection; a hidden field
 * is not answered and must not be validated as required.
 */
export function isParameterVisible(parameter: OfferingParameter, answers: AnswerMap): boolean {
  const dependency = parameter.depends_on;
  if (dependency == null) {
    return true;
  }
  return dependencyHolds(dependency, answers);
}

/**
 * The parameter as it applies right now: required outright, or while its
 * `required_when` answer holds (Ivanti's `$(Devices == "Other")` as a required
 * expression). Everything downstream — the asterisk, `isAnswerMissing` — reads
 * `required`, so a form maps its visible parameters through this once.
 */
export function withCurrentRequirement(
  parameter: OfferingParameter,
  answers: AnswerMap,
): OfferingParameter {
  const rule = parameter.required_when;
  if (parameter.required === true || rule == null) {
    return parameter;
  }
  return dependencyHolds(rule, answers) ? { ...parameter, required: true } : parameter;
}

function dependencyHolds(
  dependency: NonNullable<OfferingParameter["depends_on"]>,
  answers: AnswerMap,
): boolean {
  const parent = answerFor(answers, dependency.parameter_id);
  const selected = parent.valueIds.length > 0 ? parent.valueIds : [parent.value ?? ""];
  return (dependency.equals_value_ids ?? []).some((valueId) => selected.includes(valueId));
}

/** True when a required parameter still has nothing in it. */
export function isAnswerMissing(parameter: OfferingParameter, answer: AnswerState): boolean {
  if (parameter.required !== true) {
    return false;
  }
  if (parameter.type === "ATTACHMENT") {
    return (answer.attachmentIds ?? []).length === 0;
  }
  if (parameter.type === "MULTISELECT" || parameter.type === "SELECT") {
    return answer.valueIds.length === 0 && (answer.value ?? "").length === 0;
  }
  if (parameter.type === "BOOLEAN") {
    return answer.value !== "true";
  }
  return (answer.value ?? "").trim().length === 0;
}

/**
 * What one answer reads as in a summary: the option labels, a picked
 * colleague's name, or the text typed. Empty when unanswered.
 *
 * `employeeNames` covers the directory lookups, whose answer is a RecId that
 * appears in no option list — without it a summary would report back the GUID
 * the picker was built to hide.
 */
export function answerSummary(
  parameter: OfferingParameter,
  answer: AnswerState,
  employeeNames: Record<string, string>,
): string {
  if (isEmployeeLookup(parameter)) {
    const id = answer.valueIds[0] ?? answer.value ?? "";
    return id === "" ? "" : (employeeNames[parameter.parameter_id] ?? id);
  }
  if (answer.valueIds.length > 0) {
    const labels = answer.valueIds.map((valueId) => {
      const option = (parameter.options ?? []).find((candidate) => candidate.value_id === valueId);
      return option === undefined ? valueId : displayText(option.label);
    });
    return labels.join(", ");
  }
  return answer.value ?? "";
}

/** Parses an ISO value back into a design system date object, tolerantly. */
function toDateValue(raw: string | null, withTime: boolean): DateValue | null {
  if (raw === null || raw.length === 0) {
    return null;
  }
  try {
    return withTime ? parseDateTime(raw) : parseDate(raw);
  } catch {
    logger.debug("[tickets] unparseable offering date", raw);
    return null;
  }
}

export interface OfferingParameterFieldProps {
  parameter: OfferingParameter;
  answer: AnswerState;
  onChange: (next: AnswerState) => void;
  /** Client-side validation message, already translated. */
  error?: string;
  /**
   * Notified with the whole directory record when an employee lookup is
   * answered, so the form can fill the matching manager field. Null on clear.
   */
  onEmployeeSelect?: (employee: DirectoryEntry | null) => void;
  /**
   * True when the form wrote this field's current value for the user.
   *
   * Adds a note under the control. This is an approver on an access request,
   * so a value that appeared without anyone typing it has to say so.
   */
  autoFilled?: boolean;
  /**
   * True when the value came from what the person told the assistant.
   *
   * Adds a quiet "from your message" note, so a field that is already filled
   * in when the form opens says where that came from. Cleared by the form once
   * they edit it.
   */
  prefilled?: boolean;
  /** The note a prefilled field carries; "from your message" unless the form says otherwise. */
  prefilledNote?: string;
  /** A person lookup: told whether a name is typed but nobody picked (7 Oct). */
  onTextPendingChange?: (pending: boolean) => void;
  /**
   * Check the answer as soon as the person leaves the field (product owner,
   * 8 Oct): a date already passed, an email that does not look like one. The
   * line shows under the field until the answer is corrected; an `error` from
   * the form (a pressed Submit) still wins. Off for forms that do not run the
   * plain checks at all.
   */
  checkOnLeave?: boolean;
  /**
   * "Roles or access needed" on a form whose system is Bluestar (8 Oct): the
   * Bluestar area picker instead of the text box, writing the same text
   * (`bluestarRolesParameterId` says which field).
   */
  bluestarAreas?: boolean;
}

/** An answer as one comparable value, for the field-state tint. */
function answerValue(answer: AnswerState): readonly string[] | string | null {
  if ((answer.attachmentIds ?? []).length > 0) {
    return answer.attachmentIds ?? [];
  }
  return answer.valueIds.length > 0 ? answer.valueIds : answer.value;
}

/**
 * One offering parameter, tinted by its state (product owner, 5 Oct): blue
 * while it holds what the app filled in (the wizard, a chat card, a starter
 * step, the form's manager autofill or Ivanti's own requester default) and the
 * person has not changed it; yellow while required and empty.
 */
export function OfferingParameterField(props: OfferingParameterFieldProps) {
  const { parameter, answer, autoFilled = false, prefilled = false, checkOnLeave = false } = props;
  const checkMessage = useFormCheckMessage();
  const [touched, setTouched] = useState(false);
  const leftProblem = checkOnLeave
    ? shownCheck(checkOfferingAnswer(parameter, answer, { today: todayIso() }), { touched })
    : null;
  const error = props.error ?? checkMessage(leftProblem);
  const current = answerValue(answer);
  const fromApp =
    prefilled || autoFilled
      ? current
      : isEmployeeLookup(parameter)
        ? (parameter.default_value ?? null)
        : null;
  const state = useFieldHighlight(fromApp, current, parameter.required === true);
  return (
    <HighlightedField
      state={state}
      fieldKey={parameter.parameter_id}
      onLeave={checkOnLeave && !touched ? () => setTouched(true) : undefined}
    >
      <OfferingParameterControl {...props} error={error} />
    </HighlightedField>
  );
}

function OfferingParameterControl({
  parameter,
  answer,
  onChange,
  error,
  onEmployeeSelect,
  autoFilled = false,
  prefilled = false,
  prefilledNote,
  onTextPendingChange,
  bluestarAreas = false,
}: OfferingParameterFieldProps) {
  const { t } = useT("tickets");
  /* Only reached by the `ATTACHMENT` branch below, but declared here because a
     hook cannot live inside the switch. It nests inside the service request
     form's own paste boundary: a paste landing in this field is claimed here
     and never reaches the outer one, so the file goes to the parameter that was
     asked for rather than to the form's general attachments. */
  const paste = usePasteToAttach();

  const label = displayText(parameter.name);
  const helpText = parameter.help_text == null ? undefined : displayText(parameter.help_text);
  const autoFillNote = autoFilled
    ? t("create.managerAutoFilled")
    : prefilled
      ? (prefilledNote ?? t("create.prefilledFromChat"))
      : null;
  // `Field` wires the hint to the control with `aria-describedby`, so putting
  // the note here is what makes it reach a screen reader rather than just a
  // sighted user.
  const hint =
    autoFillNote === null ? (
      helpText
    ) : helpText === undefined ? (
      autoFillNote
    ) : (
      <>
        {helpText} {autoFillNote}
      </>
    );
  const name = `param-${parameter.parameter_id}`;
  const sortedOptions = [...(parameter.options ?? [])].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
  );
  const options = sortedOptions.map((option) => ({
    value: option.value_id,
    label: displayText(option.label),
  }));

  const common = { name, label, hint, error, required: parameter.required ?? false };

  // A directory lookup outranks the declared type: the offering still calls it
  // a `SELECT`, but its `options` are the truncated, GUID-labelled remnant this
  // picker exists to replace, so they are deliberately not rendered.
  if (isEmployeeLookup(parameter)) {
    return (
      <Field {...common}>
        <EmployeePicker
          name={name}
          value={readEmployeeId(parameter, answer)}
          defaultDisplayValue={defaultDisplayValue(parameter)}
          required={parameter.required ?? false}
          placeholder={t("employeePicker.placeholder")}
          aria-label={label}
          onTextPendingChange={onTextPendingChange}
          onChange={(employee) => {
            onChange(writeEmployeeId(parameter, employee?.id ?? null));
            onEmployeeSelect?.(employee);
          }}
        />
      </Field>
    );
  }

  const textArea = (
    <Field {...common}>
      <Textarea
        name={name}
        value={answer.value ?? ""}
        onChange={(event) => onChange({ ...answer, value: event.target.value })}
        rows={3}
        autoResize
        maxLength={parameter.max_length ?? undefined}
      />
    </Field>
  );
  const textInput = (
    <Field {...common}>
      <Input
        name={name}
        value={answer.value ?? ""}
        onChange={(event) => onChange({ ...answer, value: event.target.value })}
        maxLength={parameter.max_length ?? undefined}
      />
    </Field>
  );

  if (bluestarAreas && (parameter.type === "TEXTAREA" || parameter.type === "TEXT")) {
    return (
      <BluestarAreasField
        name={name}
        label={label}
        notes={[helpText, autoFillNote]}
        required={parameter.required ?? false}
        error={error}
        value={answer.value}
        onChange={(text) => onChange({ ...answer, value: text })}
        fallback={parameter.type === "TEXT" ? textInput : textArea}
      />
    );
  }

  // An offering can declare a parameter type this build predates; the generated
  // enum therefore leaves `type` optional. Fall back to the read-only
  // rendering rather than dropping the field.
  switch (parameter.type ?? "UNKNOWN") {
    case "TEXTAREA":
      return textArea;

    case "NUMBER":
      return (
        <Field {...common}>
          <Input
            name={name}
            type="number"
            inputMode="decimal"
            value={answer.value ?? ""}
            onChange={(event) => onChange({ ...answer, value: event.target.value })}
          />
        </Field>
      );

    case "DATE":
      return (
        <Field {...common}>
          <DatePicker
            name={name}
            granularity="day"
            value={toDateValue(answer.value, false)}
            onValueChange={(next) => onChange({ ...answer, value: next?.toString() ?? null })}
          />
        </Field>
      );

    case "DATETIME":
      return (
        <Field {...common}>
          <DateTimePicker
            name={name}
            value={toDateValue(answer.value, true)}
            onValueChange={(next) => onChange({ ...answer, value: next?.toString() ?? null })}
          />
        </Field>
      );

    case "BOOLEAN":
      return (
        <Field {...common} label={undefined}>
          <Checkbox.Root
            name={name}
            checked={answer.value === "true"}
            onCheckedChange={(checked) =>
              onChange({ ...answer, value: checked ? "true" : "false" })
            }
            label={label}
          />
        </Field>
      );

    case "SELECT":
      return (
        <Field {...common}>
          <LookupSelect
            name={name}
            items={sortedOptions.map((option) => ({
              id: option.value_id,
              label: option.label,
              code: null,
              parent_id: null,
            }))}
            value={answer.valueIds[0] ?? null}
            onValueChange={(next) =>
              onChange({ value: null, valueIds: next === null ? [] : [next] })
            }
            placeholder={t("create.selectPlaceholder")}
            aria-label={label}
          />
        </Field>
      );

    case "MULTISELECT":
      return (
        <Field {...common}>
          <FilterSelect
            options={options}
            value={answer.valueIds}
            onValueChange={(next) => onChange({ value: null, valueIds: next })}
            placeholder={t("create.selectPlaceholder")}
            // DS-GAP: FilterSelect's search box, "No matches" row and "Clear"
            // button are English defaults. Only the first is overridable, so
            // it is set here; the other two are logged in
            // docs/norican-ds-gaps.md and come out when the DS adds the props.
            searchPlaceholder={t("create.searchOptions")}
            aria-label={label}
          />
          {/* Ivanti's own form offers "Select all" here (e.g. every entity a
              business-system change affects); FilterSelect has no such row. */}
          {parameter.show_select_all === true && options.length > 1 ? (
            <div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={answer.valueIds.length === options.length}
                onClick={() =>
                  onChange({ value: null, valueIds: options.map((option) => option.value) })
                }
              >
                {t("create.selectAll")}
              </Button>
            </div>
          ) : null}
        </Field>
      );

    case "ATTACHMENT":
      return (
        <Field {...common} onPaste={paste.onPaste}>
          <AttachmentUploader
            ref={paste.ref}
            id={name}
            onChange={(attachmentIds) => onChange({ value: null, valueIds: [], attachmentIds })}
          />
        </Field>
      );

    case "UNKNOWN":
      return (
        <Field {...common}>
          <Typography variant="body-sm" tone="muted">
            {t("create.unknownParameter")}
          </Typography>
        </Field>
      );

    case "TEXT":
      return textInput;
  }
}
